import { GetMenuListResponse, ProductionCycle } from "../allocationService";
import { apiSlice } from "../apiSlice";

const API_BASE_DOMAIN = process.env.REACT_APP_API_BASE_DOMAIN_PROD;

export interface ProductionItem {
  itemId: string;
  displayName: string;
  portionSize: string;
  productId: string;
  unitPrice: number;
  prodPlanQty: number;
  classFilterTag: string;
  quantities: {
    [cycleId: string]: number;
  };
}

export interface ProductionCycleBreakdownResponse {
  productionPlanId: string;
  cycles: ProductionCycle[];
  items: ProductionItem[];
}

export interface CreateProductionCycleRequest {
  displayName: string;
  restaurantId: string;
  planId: string;
  startTime: string;
  endTime: string;
  cycleId?: string;
}

export interface CreateProductionCycleResponse {
  displayName: string;
  planId: string;
  startTime: string;
  endTime: string;
  createdAt: {
    _seconds: number;
    _nanoseconds: number;
  };
  cycleId?: string;
  success?: boolean;
  message?: string;
}

export interface ProductionCycleItemRequest {
  itemId: string;
  portionSize: "REGULAR" | "LARGE" | "SMALL";
  quantity: number;
  displayName?: string;
  classFilterTag?: string;
  productId?: string;
}

export interface ProductionCycleRequest {
  restaurantId: string;
  cycleId: string;
  items: ProductionCycleItemRequest[];
  date?: string;
}

export interface CreateProductionCyclesRequest {
  cycles: ProductionCycleRequest[];
}

export interface CreateProductionCyclesResponse {
  success?: boolean;
  message?: string;
  createdCount?: number;
}

export interface DeleteProductionCycleRequest {
  restaurantId: string;
  productionCycleId: string;
}

export interface DeleteProductionCycleResponse {
  deletedProductionId: string;
  success?: boolean;
  message?: string;
}

export interface ProductionPlanResponse {
  id: string;
  displayName: string;
  createdAt: {
    _seconds: number;
    _nanoseconds: number;
  };
  items: ProductionPlanItem[];
}

export interface ProductionPlanItem {
  itemId: string;
  displayName: string;
  plannedQty: number;
  productId: string;
  itemType: string;
  category: string;
  portionSize: string;
  unitPrice: number;
  abbreviation?: string;
  classFilterTag?: string;
  skuId?: string;
}

export interface LocalProductionPlanFormData {
  restaurantId?: string;
  items: ProductionPlanItem[];
  date?: string;
}

export interface GetProductionPlanParams {
  restaurantId: string;
  date: string;
}

// New interfaces for cart allocation
export interface CartProductAllocation {
  itemId: string;
  displayName: string;
  portionSize: string;
  classFilterTag: string;
  totalAllocated: number;
  productId: string;
  channels?: any;
}

export interface CartCycleAllocation {
  cycleId: string;
  cycleName: string;
  productionPlanId: string;
  products: CartProductAllocation[];
  isOrderPlacedForCycle?: boolean;
}

export interface CartAllocation {
  cartId: string;
  cartName: string;
  status: "ASSIGNED" | "PENDING" | "INACTIVE";
  cycles: CartCycleAllocation[];
  createdAt: {
    _seconds: number;
    _nanoseconds: number;
  };
}

export interface CartHeader {
  id: string;
  displayName: string;
  priority: string;
  abbreviation: string;
  isFixedCart: boolean;
  supportedChannels: string[];
  cartType: string;
}

export interface CartAllocationBasicPlanResponse {
  productionCycle: Array<{
    id: string;
    displayName: string;
    startTime: {
      _seconds: number;
      _nanoseconds: number;
    };
    endTime: {
      _seconds: number;
      _nanoseconds: number;
    };
    planId: string;
    createdAt: {
      _seconds: number;
      _nanoseconds: number;
    };
    items: Array<{
      itemId: string;
      portionSize: string;
      quantity: number;
      displayName: string;
      classFilterTag: string;
      productId?: string;
    }>;
  }>;
  cartAllocations: {
    id: string;
    status: string;
    carts: CartAllocation[];
    createdAt: {
      _seconds: number;
      _nanoseconds: number;
    };
    updatedAt: {
      _seconds: number;
      _nanoseconds: number;
    };
  };
  cartsHeader: CartHeader[];
}

export interface CycleReference {
  cycleId: string;
  cycleName: string;
  productionPlanId: string;
}

export interface AllocationPlan {
  totalCarts?: number;
  cycleReference: CycleReference[];
}

export interface CartProductAllocationRequest {
  productItemId: string;
  displayName: string;
  portionSize: string;
  classFilterTag: string;
  totalAllocated: number;
}

export interface CartCycleAllocationRequest {
  cycleId: string;
  cycleName: string;
  productionPlanId: string;
  products: CartProductAllocationRequest[];
}

export interface CartAllocationRequest {
  cartId: string;
  cartName: string;
  cycles: CartCycleAllocationRequest[];
}

export interface CreateCartAllocationRequest {
  restaurantId: string;
  allocationPlan: AllocationPlan;
  cartAllocations: CartAllocationRequest[];
}

export interface CreateCartAllocationResponse {
  success?: boolean;
  message?: string;
  allocationId?: string;
  createdAt?: {
    _seconds: number;
    _nanoseconds: number;
  };
}

export interface ChannelAllocation {
  allocated: number;
}

export interface ProductChannelAllocation {
  itemId: string;
  portionSize: string;
  channels: {
    delivery?: ChannelAllocation;
    pickup?: ChannelAllocation;
    dinein?: ChannelAllocation;
  };
}

export interface CycleChannelAllocation {
  cycleId: string;
  products: ProductChannelAllocation[];
}

export interface CartChannelAllocation {
  cartId: string;
  cycles: CycleChannelAllocation[];
}

export interface UpdateChannelAllocationRequest {
  restaurantId: string;
  allocationPlanId: string;
  carts: CartChannelAllocation[];
}

export interface UpdateChannelAllocationResponse {
  success?: boolean;
  message?: string;
  updatedAt?: {
    _seconds: number;
    _nanoseconds: number;
  };
}

// New interfaces for generate allocation plans
export interface GenerateAllocationPlansRequest {
  restaurantId: string;
  loadFrom: string; // ISO date string
  saveTo: string; // ISO date string
}

export interface ProductOverviewItem {
  id: string;
  displayName: string;
  itemId: string;
  planDate: {
    _seconds: number;
    _nanoseconds: number;
  };
  productId: string;
  restaurantId: string;
  updatedAt: {
    _seconds: number;
    _nanoseconds: number;
  };
  quantities: {
    allocated: number;
    produced: number;
    reserved: number;
    cancelled: number;
  };
}

export interface GetProductOverviewParams {
  restaurantId: string;
  date?: string;
}

export interface OrderItem {
  menuItemId: string;
  displayName: string;
  quantity: number;
  itemType: string;
  unitPrice: number;
}

export interface OrderRequest {
  customerId: string;
  cartId: string;
  restaurantId: string;
  displayName: string;
  address: string;
  phoneNumber: string;
  imageUrl: string;
  paymentMethod: string;
  orderCreatedBy: string;
  deliveryTime: string; // ISO string
  isScheduledOrder: boolean;
  totalPrice: number;
  orderItems: OrderItem[];
  channel: string;
  isDirectRestaurantOrder: boolean;
  isCartAllocationOrder?: boolean;
}

export interface DistributeOrderRequest {
  cycleId: string;
  orders: OrderRequest[];
}

export interface OrderRequestProduct {
  displayName: string;
  skuId: string;
  price: number;
  itemId: string;
  productId: string;
  tinyId: string;
  itemType: string;
  quantity: number;
  tier?: string;
}

// Order request payload for API
export interface CreateOrderRequestPayload {
  restaurantId: string;
  requestType: string;
  department: string;
  requestedBy: string;
  targetDate: string;
  notes?: string;
  products: OrderRequestProduct[];
  status?: string;
  totalItems?: number;
  totalQuantity?: number;
  totalAmount?: number;
}

export interface CreateOrderRequestResponse {
  success?: boolean;
  message?: string;
  orderRequestId?: string;
  error?: string;
}

export interface AcceptOrderRequestPayload {
  orderRequestId: string;
  requestStatus: "Approved" | "Rejected";
  notes?: string;
}

// ── PreProductionPlan interfaces ──────────────────────────────────────────────

export interface PrePlanSectionMenuItem {
  category: string;
  displayName: string;
  isCustomMenuItem: boolean;
  itemId: string;
  itemType: string;
  price: number;
  productId: string;
  quantity: number;
  skuId: string | null;
  tags?: {
    brand?: string[];
    class?: string[];
    cuisine?: string[];
    [key: string]: any;
  };
}

export interface PrePlanSection {
  displayName: string;
  menuItems: PrePlanSectionMenuItem[];
}

export interface PrePlanCustomMenu {
  itemId: string;
  displayName: string;
  price: number;
  productId: string;
  skuId?: string | null;
  quantity: number;
  sections: PrePlanSection[];
  menuItems?: PrePlanSectionMenuItem[]; // flat fallback items
}

export interface UpdatePreProductionPlanRequest {
  restaurantId: string;
  prePlanId: string | null; 
  customMenus: PrePlanCustomMenu[];
}

export interface UpdatePreProductionPlanResponse {
  success?: boolean;
  message?: string;
  prePlanId?: string;
}

// ─────────────────────────────────────────────────────────────────────────────

export const AllocationService = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getProductionPlan: builder.query<
      ProductionPlanResponse,
      GetProductionPlanParams
    >({
      query: ({ restaurantId, date }) => {
        const params: Record<string, string> = { restaurantId };
        if (date) params.date = date;
        return {
          url: `https://adminhttps-getrestaurantproductionplan${API_BASE_DOMAIN}`,
          params,
        };
      },
      providesTags: (result) =>
        result ? [{ type: "ProductionPlan" }] : ["ProductionPlan"],
    }),

    getProductionCycleBreakdown: builder.query<
      ProductionCycleBreakdownResponse,
      { restaurantId: string; date?: string }
    >({
      query: ({ restaurantId, date }) => {
        const params: Record<string, string> = { restaurantId };
        if (date) params.date = date;
        return {
          url: `https://adminhttps-getproductioncyclebreakdown${API_BASE_DOMAIN}`,
          params,
        };
      },
      providesTags: ["Allocations"],
    }),

    createProductionCycle: builder.mutation<
      CreateProductionCycleResponse,
      CreateProductionCycleRequest
    >({
      query: (cycleData) => ({
        url: `https://adminhttps-createproductioncycleforrestaurant${API_BASE_DOMAIN}`,
        method: "POST",
        body: cycleData,
      }),
      invalidatesTags: ["Allocations"],
      transformResponse: (response: CreateProductionCycleResponse) => ({
        ...response,
        success: response.success !== undefined ? response.success : true,
        message: response.message || "Cycle created successfully",
      }),
      transformErrorResponse: (response: { status: number; data: any }) => ({
        success: false,
        message: response.data?.message || "Failed to create cycle",
      }),
    }),

    createProductionCycles: builder.mutation<
      CreateProductionCyclesResponse,
      CreateProductionCyclesRequest
    >({
      query: (cyclesData) => ({
        url: `https://adminhttps-updatecycleproducts${API_BASE_DOMAIN}`,
        method: "POST",
        body: cyclesData,
      }),
      invalidatesTags: ["Allocations"],
      transformResponse: (
        response: CreateProductionCyclesResponse,
        meta,
        arg,
      ) => ({
        ...response,
        success: response.success !== undefined ? response.success : true,
        message: response.message || "Production cycles created successfully",
        createdCount: response.createdCount || arg.cycles.length,
      }),
      transformErrorResponse: (response: { status: number; data: any }) => ({
        success: false,
        message: response.data?.message || "Failed to create production cycles",
      }),
    }),

    deleteProductionCycle: builder.mutation<
      DeleteProductionCycleResponse,
      DeleteProductionCycleRequest
    >({
      query: ({ restaurantId, productionCycleId }) => ({
        url: `https://adminhttps-deleteproductioncycle${API_BASE_DOMAIN}`,
        method: "DELETE",
        params: { restaurantId, productionCycleId },
      }),
      invalidatesTags: ["Allocations"],
      transformResponse: (response: DeleteProductionCycleResponse) => ({
        ...response,
        success: response.success !== undefined ? response.success : true,
        message: response.message || "Production cycle deleted successfully",
      }),
      transformErrorResponse: (response: { status: number; data: any }) => ({
        success: false,
        message: response.data?.message || "Failed to delete production cycle",
        deletedProductionId: response.data?.deletedProductionId,
      }),
    }),

    createProductionPlanForAllocation: builder.mutation<
      string,
      LocalProductionPlanFormData
    >({
      query: (data) => ({
        url: `https://adminhttps-createproductionplanforrestaurant${API_BASE_DOMAIN}`,
        method: "POST",
        body: data,
        responseHandler: (response) => response.text(),
      }),
      invalidatesTags: (result, error, arg) => [
        { type: "ProductionPlans", id: arg.restaurantId },
      ],
    }),

    getCartAllocationBasicPlan: builder.query<
      CartAllocationBasicPlanResponse,
      { restaurantId: string; date?: string }
    >({
      query: ({ restaurantId, date }) => {
        const params: Record<string, string> = { restaurantId };
        if (date) params.date = date;
        return {
          url: `https://adminhttps-getcartallocationbasicplan${API_BASE_DOMAIN}`,
          params,
        };
      },
      providesTags: ["CartAllocations"],
    }),

    createCartAllocation: builder.mutation<
      CreateCartAllocationResponse,
      CreateCartAllocationRequest
    >({
      query: (allocationData) => ({
        url: `https://adminhttps-createcartallocationforrestaurant${API_BASE_DOMAIN}`,
        method: "POST",
        body: allocationData,
      }),
      invalidatesTags: ["CartAllocations", "Allocations"],
      transformResponse: (response: CreateCartAllocationResponse) => ({
        ...response,
        success: response.success !== undefined ? response.success : true,
        message: response.message || "Cart allocation created successfully",
      }),
      transformErrorResponse: (response: { status: number; data: any }) => ({
        success: false,
        message: response.data?.message || "Failed to create cart allocation",
      }),
    }),

    updateChannelAllocation: builder.mutation<
      UpdateChannelAllocationResponse,
      UpdateChannelAllocationRequest
    >({
      query: (allocationData) => ({
        url: `https://adminhttps-updatechannelallocation${API_BASE_DOMAIN}`,
        method: "POST",
        body: allocationData,
      }),
      invalidatesTags: ["CartAllocations", "Allocations"],
      transformResponse: (response: UpdateChannelAllocationResponse | null) => {
        if (!response) {
          return { success: true, message: "Channel allocation updated successfully" };
        }
        return {
          ...response,
          success: response.success !== undefined ? response.success : true,
          message: response.message || "Channel allocation updated successfully",
        };
      },
      transformErrorResponse: (response: { status: number; data: any }) => ({
        success: false,
        message: response.data?.message || "Failed to update channel allocation",
      }),
    }),

    generateAllocationPlans: builder.mutation<any, GenerateAllocationPlansRequest>({
      query: (requestData) => ({
        url: `https://adminhttps-generateallocationplans${API_BASE_DOMAIN}`,
        method: "POST",
        body: requestData,
      }),
      invalidatesTags: ["CartAllocations", "Allocations", "ProductionPlan"],
    }),

    getProductOverview: builder.query<ProductOverviewItem[], GetProductOverviewParams>({
      query: ({ restaurantId, date }) => {
        const params: Record<string, string> = { restaurantId };
        if (date) params.date = date;
        return {
          url: `https://adminhttps-getproductoverview${API_BASE_DOMAIN}`,
          params,
        };
      },
    }),

    distributeOrder: builder.mutation<any, DistributeOrderRequest>({
      query: (orderData) => ({
        url: `https://adminhttps-createordersforchannelallocation${API_BASE_DOMAIN}`,
        method: "POST",
        body: orderData,
      }),
      invalidatesTags: ["CartAllocations", "Allocations"],
      transformResponse: (response: any, meta, arg) => {
        if (response.success === false) {
          throw new Error(response.message || "Failed to distribute orders");
        }
        return {
          ...response,
          success: response.success !== undefined ? response.success : true,
          message: response.message || "Orders distributed successfully",
          totalOrdersCreated: response.totalOrdersCreated || arg.orders.length,
        };
      },
      transformErrorResponse: (response: { status: number; data: any }) => ({
        success: false,
        message:
          response.data?.message ||
          `Failed to distribute orders (Status: ${response.status})`,
        totalOrdersCreated: 0,
      }),
    }),

    createOrderRequest: builder.mutation<
      CreateOrderRequestResponse,
      CreateOrderRequestPayload
    >({
      query: (requestData) => ({
        url: `https://adminhttps-createorderrequest${API_BASE_DOMAIN}`,
        method: "POST",
        body: requestData,
      }),
      transformResponse: (response: any) => {
        console.log("Response from API:", response);
        if (!response || Object.keys(response).length === 0) {
          return { success: true, message: "Order request created successfully" };
        }
        return {
          success: response.success !== undefined ? response.success : true,
          message: response.message || "Order request created successfully",
          orderRequestId: response.orderRequestId,
        };
      },
      transformErrorResponse: (response: { status: number; data: any }) => {
        console.error("❌ Error creating order request:", response);
        if (response.status === 200) {
          return { success: true, message: "Order request created successfully" };
        }
        return {
          success: false,
          message: response.data?.message || "Failed to create order request",
          error: response.data?.error,
        };
      },
    }),

    acceptOrderRequest: builder.mutation<any, AcceptOrderRequestPayload>({
      query: (requestData) => ({
        url: `https://adminhttps-acceptordersubmitrequest${API_BASE_DOMAIN}`,
        method: "POST",
        body: requestData,
      }),
      transformResponse: (response: any, meta, arg) => {
        console.log("Accept order request response:", response);
        if (!response || Object.keys(response).length === 0) {
          return {
            success: true,
            message: "Order request accepted successfully",
            orderRequestId: arg.orderRequestId,
            requestStatus: arg.requestStatus,
          };
        }
        return {
          success: response.success !== undefined ? response.success : true,
          message: response.message || "Order request processed successfully",
          orderRequestId: response.orderRequestId || arg.orderRequestId,
          requestStatus: response.requestStatus || arg.requestStatus,
        };
      },
      transformErrorResponse: (response: { status: number; data: any }) => {
        console.error("❌ Error accepting order request:", response);
        if (response.status === 200) {
          return { success: true, message: "Order request processed successfully" };
        }
        if (response.status === 500) {
          return {
            success: false,
            message: "Server error while processing request. Please try again later.",
            error: response.data?.error || "Internal Server Error",
          };
        }
        return {
          success: false,
          message: response.data?.message || "Failed to accept order request",
          error: response.data?.error,
        };
      },
    }),

    getPrePlanList: builder.query<GetMenuListResponse, any>({
      query: ({ restaurantId }) => ({
        url: `https://adminhttps-getmenulistforpreplan${API_BASE_DOMAIN}`,
        params: { restaurantId },
      }),
      transformResponse: (response: GetMenuListResponse) => ({
        setMenus: [...response.setMenus].sort(
          (a, b) => b.createdAt._seconds - a.createdAt._seconds
        ),
        customMenus: [...response.customMenus].sort(
          (a, b) => b.createdAt._seconds - a.createdAt._seconds
        ),
        orderRequests: response.orderRequests,
      }),
    }),

    updatePreProductionPlan: builder.mutation<
      UpdatePreProductionPlanResponse,
      UpdatePreProductionPlanRequest
    >({
      query: (requestData) => ({
        url: `https://adminhttps-updatepreproductionplan${API_BASE_DOMAIN}`,
        method: "POST",
        body: requestData,
      }),
      transformResponse: (response: any) => {
        if (!response || Object.keys(response).length === 0) {
          return { success: true, message: "Pre production plan updated successfully" };
        }
        return {
          success: response.success !== undefined ? response.success : true,
          message: response.message || "Pre production plan updated successfully",
          prePlanId: response.prePlanId,
        };
      },
      transformErrorResponse: (response: { status: number; data: any }) => {
        console.error("❌ Error updating pre production plan:", response);
        if (response.status === 200) {
          return { success: true, message: "Pre production plan updated successfully" };
        }
        return {
          success: false,
          message: response.data?.message || "Failed to update pre production plan",
          error: response.data?.error,
        };
      },
    }),
  }),
});

export const {
  useLazyGetProductionCycleBreakdownQuery,
  useCreateProductionCycleMutation,
  useCreateProductionCyclesMutation,
  useDeleteProductionCycleMutation,
  useLazyGetProductionPlanQuery,
  useCreateProductionPlanForAllocationMutation,
  useGetCartAllocationBasicPlanQuery,
  useLazyGetCartAllocationBasicPlanQuery,
  useCreateCartAllocationMutation,
  useUpdateChannelAllocationMutation,
  useGenerateAllocationPlansMutation,
  useLazyGetProductOverviewQuery,
  useGetProductOverviewQuery,
  useDistributeOrderMutation,
  useCreateOrderRequestMutation,
  useAcceptOrderRequestMutation,
  useLazyGetPrePlanListQuery,
  useGetPrePlanListQuery,
  useUpdatePreProductionPlanMutation,
} = AllocationService;